import type {
  GameDefinition,
  LeaderboardEntry,
  QuizRoundDefinition,
  QuizRoundResult,
  RoundDefinition,
  RoundResult,
  SimulatedParticipant,
  VoteHighlight,
  VoteRoundDefinition,
  VoteRoundResult,
} from "./types";

function createQuizResult(
  round: QuizRoundDefinition,
  participants: readonly SimulatedParticipant[],
  roundIndex: number,
  answerOverrides: Readonly<Record<string, string>>,
): QuizRoundResult {
  const correctIndex = round.options.findIndex(
    (option) => option.id === round.correctOptionId,
  );

  return {
    roundId: round.id,
    type: "quiz",
    answers: participants.map((participant, participantIndex) => {
      const overriddenOptionId = answerOverrides[participant.id];
      const validOverriddenOptionId = round.options.some(
        (option) => option.id === overriddenOptionId,
      )
        ? overriddenOptionId
        : undefined;
      const answersCorrectly = validOverriddenOptionId
        ? validOverriddenOptionId === round.correctOptionId
        : (participantIndex + roundIndex) % 3 !== 1;
      const fallbackIndex =
        (correctIndex + participantIndex + roundIndex + 1) %
        round.options.length;
      const optionId =
        validOverriddenOptionId ??
        (answersCorrectly
          ? round.correctOptionId
          : (round.options[fallbackIndex]?.id ?? round.correctOptionId));
      const awardedPoints = answersCorrectly
        ? Math.max(
            Math.round(round.maxPoints * 0.55),
            round.maxPoints - participantIndex * 55 - roundIndex * 35,
          )
        : 0;

      return {
        participantId: participant.id,
        optionId,
        awardedPoints,
      };
    }),
  };
}

function createVoteResult(
  round: VoteRoundDefinition,
  participants: readonly SimulatedParticipant[],
  roundIndex: number,
  answerOverrides: Readonly<Record<string, string>>,
): VoteRoundResult {
  const preferredTargetIndex = (roundIndex * 2) % participants.length;

  return {
    roundId: round.id,
    type: "vote",
    votes: participants.map((participant, participantIndex) => {
      const overriddenTargetId = answerOverrides[participant.id];
      const validOverriddenTargetId = participants.some(
        (candidate) => candidate.id === overriddenTargetId,
      )
        ? overriddenTargetId
        : undefined;
      const targetIndex =
        participantIndex === preferredTargetIndex
          ? (preferredTargetIndex + 1) % participants.length
          : preferredTargetIndex;

      return {
        participantId: participant.id,
        targetParticipantId:
          validOverriddenTargetId ??
          participants[targetIndex]?.id ??
          participant.id,
      };
    }),
  };
}

export function createRoundResult(
  round: RoundDefinition,
  participants: readonly SimulatedParticipant[],
  roundIndex: number,
  answerOverrides: Readonly<Record<string, string>> = {},
): RoundResult {
  return round.type === "quiz"
    ? createQuizResult(round, participants, roundIndex, answerOverrides)
    : createVoteResult(round, participants, roundIndex, answerOverrides);
}

export function getLeaderboard(
  participants: readonly SimulatedParticipant[],
  results: readonly RoundResult[],
): LeaderboardEntry[] {
  return participants
    .map((participant) => {
      const answers = results.flatMap((result) =>
        result.type === "quiz"
          ? result.answers.filter(
              (answer) => answer.participantId === participant.id,
            )
          : [],
      );

      return {
        participant,
        score: answers.reduce(
          (total, answer) => total + answer.awardedPoints,
          0,
        ),
        correctAnswers: answers.filter((answer) => answer.awardedPoints > 0)
          .length,
      };
    })
    .sort(
      (first, second) =>
        second.score - first.score ||
        first.participant.name.localeCompare(second.participant.name, "sv"),
    );
}

export function getVoteHighlights(
  definition: GameDefinition,
  participants: readonly SimulatedParticipant[],
  results: readonly RoundResult[],
): VoteHighlight[] {
  return results.flatMap((result) => {
    if (result.type !== "vote") {
      return [];
    }

    const round = definition.rounds.find(
      (candidate) => candidate.id === result.roundId,
    );

    if (!round || round.type !== "vote") {
      return [];
    }

    const counts = new Map<string, number>();
    result.votes.forEach((vote) => {
      counts.set(
        vote.targetParticipantId,
        (counts.get(vote.targetParticipantId) ?? 0) + 1,
      );
    });

    const winner = participants
      .map((participant) => ({
        participant,
        votes: counts.get(participant.id) ?? 0,
      }))
      .sort(
        (first, second) =>
          second.votes - first.votes ||
          first.participant.name.localeCompare(second.participant.name, "sv"),
      )[0];

    return winner
      ? [
          {
            roundId: result.roundId,
            prompt: round.prompt,
            participant: winner.participant,
            votes: winner.votes,
          },
        ]
      : [];
  });
}

export function getOptionCounts(
  round: QuizRoundDefinition,
  result: QuizRoundResult,
): ReadonlyMap<string, number> {
  const counts = new Map(round.options.map((option) => [option.id, 0]));

  result.answers.forEach((answer) => {
    counts.set(answer.optionId, (counts.get(answer.optionId) ?? 0) + 1);
  });

  return counts;
}

export function getVoteCounts(
  participants: readonly SimulatedParticipant[],
  result: VoteRoundResult,
): Array<{ participant: SimulatedParticipant; votes: number }> {
  const counts = new Map<string, number>();

  result.votes.forEach((vote) => {
    counts.set(
      vote.targetParticipantId,
      (counts.get(vote.targetParticipantId) ?? 0) + 1,
    );
  });

  return participants
    .map((participant) => ({
      participant,
      votes: counts.get(participant.id) ?? 0,
    }))
    .sort(
      (first, second) =>
        second.votes - first.votes ||
        first.participant.name.localeCompare(second.participant.name, "sv"),
    );
}
